import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Plus, Package2, Minus, PlusCircle, Trash2, AlertTriangle, Boxes,
  IndianRupee, TrendingUp, TrendingDown, Download, History,
} from 'lucide-react';
import { toast } from 'sonner';
import InventoryTrends from '@/components/InventoryTrends';
import InventoryHistorySheet from '@/components/InventoryHistorySheet';
import ConfirmSheet from '@/components/ConfirmSheet';

const CATEGORIES = [
  { id: 'vegetables', name: 'Vegetables' },
  { id: 'fruits', name: 'Fruits' },
  { id: 'grains', name: 'Grains & Cereals' },
  { id: 'pulses', name: 'Pulses & Lentils' },
  { id: 'spices', name: 'Spices' },
  { id: 'dairy', name: 'Dairy' },
  { id: 'oilseeds', name: 'Oilseeds' },
  { id: 'other', name: 'Other' },
];

const UNIT_LABELS = ['kg', 'gms', 'liter', 'ml', 'pcs', 'dozen', 'pack'];

const LOW_STOCK = 10;

const inr = (n) => `₹${(Math.round((n || 0) * 100) / 100).toLocaleString('en-IN')}`;

function InventoryCard({ item, onAdjust, onDelete, onHistory }) {
  const cat = CATEGORIES.find(c => c.id === item.category);
  const low = item.units <= LOW_STOCK;
  const currentStockValue = (item.units || 0) * (item.avg_cost_per_unit || 0);
  const profit = item.total_profit || 0;
  const ProfitIcon = profit >= 0 ? TrendingUp : TrendingDown;
  const profitColor = profit > 0 ? 'text-[#15803D]' : profit < 0 ? 'text-[#B91C1C]' : 'text-[#5B6675]';

  return (
    <div
      data-testid={`inventory-card-${item.id}`}
      className="bg-[#F6F8FB] border border-[#E4E8EF] rounded-xl p-3"
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
          low ? 'bg-[#B91C1C]/10' : 'bg-[#0B4F3F]/10'
        }`}>
          <Package2 className={`w-5 h-5 ${low ? 'text-[#B91C1C]' : 'text-[#0B4F3F]'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-[#0B1620] truncate">{item.variant_name}</p>
              <p className="text-[11px] text-[#5B6675]">
                {cat?.name || item.category} · {item.unit_size} {item.unit_size_label}/unit
              </p>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                data-testid={`history-inv-${item.id}`}
                onClick={() => onHistory(item)}
                className="text-[#5B6675] hover:text-[#0B4F3F] p-0.5"
                aria-label="View transaction history"
                title="View history"
              >
                <History className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                data-testid={`delete-inv-${item.id}`}
                onClick={() => onDelete(item)}
                className="text-[#5B6675] hover:text-[#B91C1C] p-0.5"
                aria-label="Delete inventory item"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          <div className="mt-2 flex items-baseline gap-1">
            <span className={`text-xl font-bold font-['Bricolage_Grotesque'] ${low ? 'text-[#B91C1C]' : 'text-[#0B1620]'}`}>
              {item.units}
            </span>
            <span className="text-xs text-[#5B6675]">units left</span>
            {low && (
              <span className="ml-1.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#B91C1C]/10 text-[#B91C1C] flex items-center gap-0.5">
                <AlertTriangle className="w-2.5 h-2.5" /> LOW
              </span>
            )}
          </div>

          {/* Money summary */}
          <div className="mt-2 grid grid-cols-2 gap-2 p-2 rounded-lg bg-[#F6F8FB]">
            <div>
              <p className="text-[9px] uppercase tracking-wide text-[#5B6675]">Current stock value</p>
              <p className="text-sm font-semibold text-[#0B1620]" data-testid={`stock-value-${item.id}`}>
                {inr(currentStockValue)}
              </p>
              <p className="text-[10px] text-[#5B6675]/80">
                avg {inr(item.avg_cost_per_unit)}/unit
              </p>
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-wide text-[#5B6675]">Profit / loss</p>
              <p
                className={`text-sm font-semibold flex items-center gap-1 ${profitColor}`}
                data-testid={`profit-${item.id}`}
              >
                <ProfitIcon className="w-3 h-3" />
                {inr(profit)}
              </p>
              <p className="text-[10px] text-[#5B6675]/80">
                on {inr(item.total_revenue || 0)} revenue
              </p>
            </div>
          </div>

          <div className="mt-2 flex gap-2">
            <Button
              type="button"
              size="sm"
              data-testid={`add-stock-${item.id}`}
              onClick={() => onAdjust(item, 'add')}
              className="flex-1 h-8 bg-[#0B4F3F]/5 hover:bg-[#0B4F3F]/10 text-[#0B4F3F] text-xs rounded-lg border border-[#0B4F3F]/20"
            >
              <PlusCircle className="w-3.5 h-3.5 mr-1" /> Add stock
            </Button>
            <Button
              type="button"
              size="sm"
              data-testid={`sell-stock-${item.id}`}
              onClick={() => onAdjust(item, 'sell')}
              disabled={item.units <= 0}
              className="flex-1 h-8 bg-[#0B4F3F]/5 hover:bg-[#0B4F3F]/10 text-[#0B4F3F] text-xs rounded-lg border border-[#0B4F3F]/20 disabled:opacity-50"
            >
              <Minus className="w-3.5 h-3.5 mr-1" /> Mark sold
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminInventory() {
  const { api } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    category: 'fruits',
    variant_name: '',
    units: '',
    unit_size: '',
    unit_size_label: 'kg',
    purchase_cost_per_unit: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  // Adjust dialog
  const [adjustTarget, setAdjustTarget] = useState(null); // { item, mode: 'add'|'sell' }
  const [adjustUnits, setAdjustUnits] = useState('');
  const [adjustPrice, setAdjustPrice] = useState('');  // cost for add, sale price for sell
  const [adjustNotes, setAdjustNotes] = useState('');
  const [adjusting, setAdjusting] = useState(false);

  // History sheet
  const [historyId, setHistoryId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/inventory');
      setItems(data.items || []);
      setRefreshKey((k) => k + 1);
    } catch {
      toast.error("Couldn't load inventory");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => setForm({
    category: 'fruits', variant_name: '', units: '', unit_size: '',
    unit_size_label: 'kg', purchase_cost_per_unit: '', notes: '',
  });

  const handleCreate = async (e) => {
    e.preventDefault();
    const units = parseFloat(form.units);
    const unitSize = parseFloat(form.unit_size);
    const cost = parseFloat(form.purchase_cost_per_unit);
    if (!form.variant_name.trim() || !(units >= 0) || !(unitSize > 0) || !(cost >= 0)) {
      toast.error('Fill all fields with valid numbers');
      return;
    }
    setSaving(true);
    try {
      await api.post('/admin/inventory', {
        category: form.category,
        variant_name: form.variant_name.trim(),
        units,
        unit_size: unitSize,
        unit_size_label: form.unit_size_label,
        purchase_cost_per_unit: cost,
        notes: form.notes || null,
      });
      toast.success(`Added ${form.variant_name} to inventory`);
      setCreateOpen(false);
      resetForm();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add inventory');
    } finally {
      setSaving(false);
    }
  };

  const openAdjust = (item, mode) => {
    setAdjustTarget({ item, mode });
    setAdjustUnits('');
    setAdjustPrice('');
    setAdjustNotes('');
  };

  const handleAdjust = async (e) => {
    e.preventDefault();
    if (!adjustTarget) return;
    const units = parseFloat(adjustUnits);
    const price = parseFloat(adjustPrice);
    if (!(units > 0)) {
      toast.error('Enter a positive quantity');
      return;
    }
    if (!(price >= 0)) {
      toast.error(adjustTarget.mode === 'add' ? 'Enter the purchase cost per unit' : 'Enter the sale price per unit');
      return;
    }
    setAdjusting(true);
    try {
      const path = adjustTarget.mode === 'add' ? 'add' : 'sell';
      const body = adjustTarget.mode === 'add'
        ? { units, purchase_cost_per_unit: price, notes: adjustNotes || null }
        : { units, sale_price_per_unit: price, notes: adjustNotes || null };
      const { data } = await api.post(`/admin/inventory/${adjustTarget.item.id}/${path}`, body);

      if (adjustTarget.mode === 'add') {
        toast.success(`Added ${units} units · spent ${inr(units * price)}`);
      } else {
        const p = data.sale?.profit || 0;
        const word = p > 0 ? `Profit ${inr(p)}` : p < 0 ? `Loss ${inr(Math.abs(p))}` : 'Break-even';
        toast.success(`Sold ${units} units · revenue ${inr(data.sale?.revenue)} · ${word}`);
      }
      setAdjustTarget(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update stock');
    } finally {
      setAdjusting(false);
    }
  };

  const handleDelete = async (item) => {
    setDeleteTarget(item);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeletingId(deleteTarget.id);
    try {
      await api.delete(`/admin/inventory/${deleteTarget.id}`);
      toast.success('Inventory item deleted');
      setDeleteTarget(null);
      await load();
    } catch {
      toast.error('Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get('/admin/inventory/export.csv', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.download = `frootex-inventory-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Inventory exported as CSV');
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  // Preview for adjust dialog
  const adjustUnitsNum = parseFloat(adjustUnits) || 0;
  const adjustPriceNum = parseFloat(adjustPrice) || 0;
  const adjustPreview = adjustTarget && adjustUnitsNum > 0 ? (() => {
    if (adjustTarget.mode === 'add') {
      return { totalSpend: adjustUnitsNum * adjustPriceNum };
    }
    const avg = adjustTarget.item.avg_cost_per_unit || 0;
    const cogs = adjustUnitsNum * avg;
    const revenue = adjustUnitsNum * adjustPriceNum;
    return {
      revenue, cogs,
      profit: revenue - cogs,
      avgCost: avg,
    };
  })() : null;

  // Summary
  const totalItems = items.length;
  const totalUnits = items.reduce((s, i) => s + (i.units || 0), 0);
  const lowStock = items.filter(i => (i.units || 0) <= LOW_STOCK).length;
  const totalInvested = items.reduce((s, i) => s + (i.total_invested || 0), 0);
  const totalRevenue = items.reduce((s, i) => s + (i.total_revenue || 0), 0);
  const totalProfit = items.reduce((s, i) => s + (i.total_profit || 0), 0);

  return (
    <div className="space-y-4" data-testid="admin-inventory">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] p-2.5 text-center">
          <Boxes className="w-4 h-4 mx-auto text-[#0B4F3F] mb-1" />
          <p className="text-base font-bold text-[#0B1620] font-['Bricolage_Grotesque']" data-testid="inv-total-items">{totalItems}</p>
          <p className="text-[10px] text-[#5B6675]">Items</p>
        </div>
        <div className="bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] p-2.5 text-center">
          <Package2 className="w-4 h-4 mx-auto text-[#0B4F3F] mb-1" />
          <p className="text-base font-bold text-[#0B1620] font-['Bricolage_Grotesque']" data-testid="inv-total-units">{totalUnits}</p>
          <p className="text-[10px] text-[#5B6675]">Units</p>
        </div>
        <div className="bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] p-2.5 text-center">
          <AlertTriangle className={`w-4 h-4 mx-auto mb-1 ${lowStock > 0 ? 'text-[#B91C1C]' : 'text-[#5B6675]'}`} />
          <p className={`text-base font-bold font-['Bricolage_Grotesque'] ${lowStock > 0 ? 'text-[#B91C1C]' : 'text-[#0B1620]'}`} data-testid="inv-low-stock">
            {lowStock}
          </p>
          <p className="text-[10px] text-[#5B6675]">Low stock</p>
        </div>
      </div>

      {/* Money summary */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] p-2.5 text-center">
          <IndianRupee className="w-4 h-4 mx-auto text-[#5B6675] mb-1" />
          <p className="text-sm font-bold text-[#0B1620] font-['Bricolage_Grotesque']">{inr(totalInvested)}</p>
          <p className="text-[10px] text-[#5B6675]">Invested</p>
        </div>
        <div className="bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] p-2.5 text-center">
          <IndianRupee className="w-4 h-4 mx-auto text-[#0B4F3F] mb-1" />
          <p className="text-sm font-bold text-[#0B1620] font-['Bricolage_Grotesque']">{inr(totalRevenue)}</p>
          <p className="text-[10px] text-[#5B6675]">Revenue</p>
        </div>
        <div className="bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] p-2.5 text-center">
          {totalProfit >= 0
            ? <TrendingUp className="w-4 h-4 mx-auto text-[#15803D] mb-1" />
            : <TrendingDown className="w-4 h-4 mx-auto text-[#B91C1C] mb-1" />
          }
          <p
            className={`text-sm font-bold font-['Bricolage_Grotesque'] ${totalProfit > 0 ? 'text-[#15803D]' : totalProfit < 0 ? 'text-[#B91C1C]' : 'text-[#0B1620]'}`}
            data-testid="inv-total-profit"
          >
            {inr(totalProfit)}
          </p>
          <p className="text-[10px] text-[#5B6675]">Net P/L</p>
        </div>
      </div>

      {/* Add button */}
      <div className="flex gap-2">
        <Button
          type="button"
          data-testid="new-inventory-btn"
          onClick={() => setCreateOpen(true)}
          className="flex-1 h-11 bg-[#0B4F3F] hover:bg-[#083D31] text-[#F6F8FB] rounded-full font-semibold"
        >
          <Plus className="w-4 h-4 mr-1" /> Add inventory item
        </Button>
        <Button
          type="button"
          data-testid="export-csv-btn"
          onClick={handleExport}
          disabled={exporting || items.length === 0}
          variant="outline"
          className="h-11 rounded-full border-[#E4E8EF] text-[#0B1620] hover:bg-[#EFF2F7] px-4"
          title={items.length === 0 ? 'Add items first' : 'Export inventory P/L as CSV'}
        >
          <Download className="w-4 h-4 mr-1" />
          {exporting ? 'Exporting…' : 'CSV'}
        </Button>
      </div>

      {/* Trends */}
      {items.length > 0 && <InventoryTrends refreshKey={refreshKey} />}

      {/* List */}
      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-32 bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-10">
          <Boxes className="w-10 h-10 mx-auto text-[#5B6675]/30 mb-2" />
          <p className="text-sm text-[#5B6675]">No inventory yet.</p>
          <p className="text-xs text-[#5B6675]/70 mt-1">Tap "Add inventory item" to create your first entry.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="inventory-list">
          {items.map(item => (
            <InventoryCard
              key={item.id}
              item={item}
              onAdjust={openAdjust}
              onDelete={handleDelete}
              onHistory={(it) => setHistoryId(it.id)}
            />
          ))}
        </div>
      )}

      {/* History sheet */}
      {historyId && (
        <InventoryHistorySheet
          inventoryId={historyId}
          onClose={() => setHistoryId(null)}
        />
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md w-[calc(100vw-2rem)] rounded-2xl p-0 max-h-[90vh] flex flex-col" data-testid="create-inventory-dialog">
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-[#E4E8EF]">
            <DialogTitle>Add inventory item</DialogTitle>
            <DialogDescription>
              Add a new produce variant to your stock.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
            <div>
              <label className="text-xs font-medium text-[#5B6675] block mb-1">Category *</label>
              <Select
                value={form.category}
                onValueChange={(v) => setForm(f => ({ ...f, category: v }))}
              >
                <SelectTrigger data-testid="inv-category-select" className="h-10 bg-[#F6F8FB] border-[#E4E8EF] rounded-lg">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs font-medium text-[#5B6675] block mb-1">Variant name *</label>
              <Input
                data-testid="inv-variant-input"
                value={form.variant_name}
                onChange={(e) => setForm(f => ({ ...f, variant_name: e.target.value }))}
                placeholder="e.g., Alphonso Mango"
                className="h-10 bg-[#F6F8FB] border-[#E4E8EF] rounded-lg"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium text-[#5B6675] block mb-1">No. of units *</label>
                <Input
                  data-testid="inv-units-input"
                  type="number" step="any" min="0"
                  value={form.units}
                  onChange={(e) => setForm(f => ({ ...f, units: e.target.value }))}
                  placeholder="100"
                  className="h-10 bg-[#F6F8FB] border-[#E4E8EF] rounded-lg"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[#5B6675] block mb-1">Per unit size *</label>
                <div className="flex gap-1">
                  <Input
                    data-testid="inv-unitsize-input"
                    type="number" step="any" min="0"
                    value={form.unit_size}
                    onChange={(e) => setForm(f => ({ ...f, unit_size: e.target.value }))}
                    placeholder="500"
                    className="h-10 bg-[#F6F8FB] border-[#E4E8EF] rounded-lg flex-1"
                  />
                  <Select
                    value={form.unit_size_label}
                    onValueChange={(v) => setForm(f => ({ ...f, unit_size_label: v }))}
                  >
                    <SelectTrigger data-testid="inv-unitlabel-select" className="h-10 bg-[#F6F8FB] border-[#E4E8EF] rounded-lg w-[86px] flex-shrink-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {UNIT_LABELS.map(u => (
                        <SelectItem key={u} value={u}>{u}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-[#5B6675] block mb-1">Purchase cost per unit (₹) *</label>
              <Input
                data-testid="inv-cost-input"
                type="number" step="any" min="0"
                value={form.purchase_cost_per_unit}
                onChange={(e) => setForm(f => ({ ...f, purchase_cost_per_unit: e.target.value }))}
                placeholder="e.g., 50"
                className="h-10 bg-[#F6F8FB] border-[#E4E8EF] rounded-lg"
              />
              {parseFloat(form.units) > 0 && parseFloat(form.purchase_cost_per_unit) >= 0 && (
                <p className="text-[11px] text-[#5B6675] mt-1" data-testid="inv-cost-preview">
                  Total spend on this purchase:{' '}
                  <span className="font-semibold text-[#0B1620]">
                    {inr(parseFloat(form.units) * parseFloat(form.purchase_cost_per_unit))}
                  </span>
                </p>
              )}
            </div>

            <div>
              <label className="text-xs font-medium text-[#5B6675] block mb-1">Notes (optional)</label>
              <Textarea
                data-testid="inv-notes-input"
                value={form.notes}
                onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Supplier, invoice no., etc."
                rows={2}
                className="text-sm bg-[#F6F8FB] border-[#E4E8EF] rounded-lg resize-none"
              />
            </div>

            </div>

            <DialogFooter className="px-5 py-3 border-t border-[#E4E8EF] gap-2 sm:gap-2">
              <Button
                type="button" variant="ghost"
                onClick={() => { setCreateOpen(false); resetForm(); }}
                className="h-10 rounded-full"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="inv-submit-btn"
                disabled={saving}
                className="h-10 bg-[#0B4F3F] hover:bg-[#083D31] text-[#F6F8FB] rounded-full font-semibold"
              >
                {saving ? 'Saving…' : 'Add to inventory'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Adjust dialog */}
      <Dialog open={!!adjustTarget} onOpenChange={(o) => !o && setAdjustTarget(null)}>
        <DialogContent className="max-w-sm w-[calc(100vw-2rem)] rounded-2xl p-0 max-h-[90vh] flex flex-col" data-testid="adjust-inventory-dialog">
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-[#E4E8EF]">
            <DialogTitle>
              {adjustTarget?.mode === 'add' ? 'Add stock' : 'Mark as sold'}
            </DialogTitle>
            <DialogDescription>
              {adjustTarget?.item && (
                <>
                  {adjustTarget.item.variant_name} · stock:{' '}
                  <span className="font-semibold text-[#0B1620]">{adjustTarget.item.units} units</span>
                  {' · '}avg cost:{' '}
                  <span className="font-semibold text-[#0B1620]">{inr(adjustTarget.item.avg_cost_per_unit)}</span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAdjust} className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
            <div>
              <label className="text-xs font-medium text-[#5B6675] block mb-1">
                {adjustTarget?.mode === 'add' ? 'Units received' : 'Units sold'} *
              </label>
              <Input
                data-testid="adjust-units-input"
                type="number" step="any" min="0"
                value={adjustUnits}
                onChange={(e) => setAdjustUnits(e.target.value)}
                placeholder="e.g., 40"
                autoFocus
                className="h-10 bg-[#F6F8FB] border-[#E4E8EF] rounded-lg"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[#5B6675] block mb-1">
                {adjustTarget?.mode === 'add' ? 'Purchase cost per unit (₹) *' : 'Sale price per unit (₹) *'}
              </label>
              <Input
                data-testid="adjust-price-input"
                type="number" step="any" min="0"
                value={adjustPrice}
                onChange={(e) => setAdjustPrice(e.target.value)}
                placeholder={adjustTarget?.mode === 'add' ? 'e.g., 50' : 'e.g., 80'}
                className="h-10 bg-[#F6F8FB] border-[#E4E8EF] rounded-lg"
              />
            </div>

            {/* Live preview */}
            {adjustPreview && adjustTarget.mode === 'add' && (
              <div className="p-2.5 rounded-lg bg-[#F6F8FB] border border-[#E4E8EF] text-xs" data-testid="adjust-preview">
                <div className="flex justify-between">
                  <span className="text-[#5B6675]">Total spend</span>
                  <span className="font-semibold text-[#0B1620]">{inr(adjustPreview.totalSpend)}</span>
                </div>
              </div>
            )}
            {adjustPreview && adjustTarget.mode === 'sell' && (
              <div className="p-2.5 rounded-lg bg-[#F6F8FB] border border-[#E4E8EF] text-xs space-y-1" data-testid="adjust-preview">
                <div className="flex justify-between">
                  <span className="text-[#5B6675]">Revenue</span>
                  <span className="font-semibold text-[#0B1620]">{inr(adjustPreview.revenue)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#5B6675]">Cost basis ({inr(adjustPreview.avgCost)}/unit)</span>
                  <span className="font-semibold text-[#0B1620]">−{inr(adjustPreview.cogs)}</span>
                </div>
                <div className="flex justify-between pt-1 border-t border-[#E4E8EF]">
                  <span className="text-[#5B6675] font-medium">Estimated {adjustPreview.profit >= 0 ? 'profit' : 'loss'}</span>
                  <span
                    className={`font-bold ${adjustPreview.profit > 0 ? 'text-[#15803D]' : adjustPreview.profit < 0 ? 'text-[#B91C1C]' : 'text-[#0B1620]'}`}
                    data-testid="estimated-profit"
                  >
                    {inr(adjustPreview.profit)}
                  </span>
                </div>
              </div>
            )}

            <div>
              <label className="text-xs font-medium text-[#5B6675] block mb-1">Notes (optional)</label>
              <Textarea
                data-testid="adjust-notes-input"
                value={adjustNotes}
                onChange={(e) => setAdjustNotes(e.target.value)}
                placeholder={adjustTarget?.mode === 'add' ? 'Supplier / invoice' : 'Buyer / invoice'}
                rows={2}
                className="text-sm bg-[#F6F8FB] border-[#E4E8EF] rounded-lg resize-none"
              />
            </div>

            </div>

            <DialogFooter className="px-5 py-3 border-t border-[#E4E8EF] gap-2 sm:gap-2">
              <Button
                type="button" variant="ghost"
                onClick={() => setAdjustTarget(null)}
                className="h-10 rounded-full"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="adjust-submit-btn"
                disabled={adjusting}
                className={`h-10 rounded-full font-semibold text-[#F6F8FB] ${
                  adjustTarget?.mode === 'add'
                    ? 'bg-[#0B4F3F] hover:bg-[#083D31]'
                    : 'bg-[#0B4F3F] hover:bg-[#B91C1C]'
                }`}
              >
                {adjusting
                  ? 'Saving…'
                  : (adjustTarget?.mode === 'add' ? 'Add to stock' : 'Confirm sale')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <ConfirmSheet
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete inventory item?"
        description={deleteTarget ? `"${deleteTarget.variant_name}" will be removed. Transaction history is preserved.` : ''}
        confirmLabel="Delete"
        cancelLabel="Keep it"
        destructive
        icon={<Trash2 className="w-6 h-6" strokeWidth={2.2} />}
        onConfirm={confirmDelete}
        loading={!!(deleteTarget && deletingId === deleteTarget.id)}
        testId="delete-inventory"
      />
    </div>
  );
}
